// Расчётный лист: почему на руки вышло именно столько.
//
// Общей суммы по нескольким юрлицам в этих типах нет намеренно — вместо неё `total_note`
// словами. Одно число читалось бы как «ему должны столько» и оказалось бы неправдой у кассы:
// платят по каждому юрлицу отдельно, в свои дни.

import type { FileResponse } from '@/shared/api/client';
import { request, requestFile } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';

export interface PayslipTariffLine {
  post_name?: string | null;
  row_type: string;
  /** Часы, норма и ставка приходят строкой: дробное по сети не передаётся числом. */
  hours?: string | null;
  norm_hours?: string | null;
  rate?: string | null;
  period_amount?: number | null;
  accrued: number;
  is_excluded: boolean;
  exclusion_basis?: string | null;
}

export interface PayslipDeduction {
  column_code: string;
  /** Название по-русски приходит с сервера: словарь живёт в контракте, копий не держим. */
  column_title: string;
  amount: number;
  /** Основание видно не всем: в личном листе его нет (сценарий 81). */
  basis?: string | null;
}

export interface PayslipDebt {
  debt_type: string;
  debt_type_title: string;
  description?: string | null;
  initial_amount: number;
  outstanding: number;
  schedule_value?: number | null;
  /** Как удерживается словами: «1 000 ₽ в месяц», «весь остаток ближайшим расчётом». */
  schedule_note?: string;
  /** Пояснение к долгу: без него удержание читается как произвол. */
  note?: string | null;
}

export interface PayslipAdvancePayout {
  /** Календарная дата выдачи в виде «2026-05-07». */
  happened_on: string;
  amount: number;
  title?: string | null;
}

export interface PayslipAdvance {
  period_year: number;
  period_month: number;
  amount: number;
  legal_entity_name: string;
  /**
   * Из чего сложился аванс месяца. Заказчик просил историю «с указанием дат и сумм»
   * дословно; до 06.09.2026 в строке стояли только месяц и год.
   */
  payouts: PayslipAdvancePayout[];
}

export interface PayslipPart {
  payroll_id: string;
  payroll_employee_id: string;
  legal_entity_id: string;
  legal_entity_name: string;
  site_name?: string | null;
  status: string;
  number?: string | null;
  hours_fact: string;
  hours_norm?: string | null;
  /**
   * Проставлены ли часы вообще. Ноль часов и НЕ ЗАПОЛНЕННЫЕ часы — разные факты: «0 из 558»
   * человек читает как «я не выходил», а правда — «табель за месяц ещё не закрыли».
   * Поле необязательное: старый ответ сервера его не содержит, и тогда считаем, что часы есть.
   */
  hours_filled?: boolean;
  /** Проводился ли расчёт по человеку. Нет — суммы в листе не нули, а неизвестные. */
  calculated?: boolean;
  /**
   * Почему денег в листе нет или им нельзя верить: «не заполнен тариф», «часы за месяц ещё не
   * проставлены». Пусто — числа правдивы. Необязательное: старый ответ его не содержит.
   */
  not_counted?: string | null;
  accruals_total: number;
  /** Официальная часть одним числом. Приходит всем: без неё лист не сходится. */
  official_total: number;
  /**
   * Разбивка официальной части. У ролей со скрытием ключей в ответе НЕТ ВОВСЕ (тз 5.7), и
   * отличать «не показывают» от «не платили» нужно именно по отсутствию, а не по нулю.
   */
  official_paid?: number;
  official_rest?: number;
  advance_total: number;
  total_payout: number;
  /** Итог обнулён каскадом: не «не положено», а «не хватило начисленного». */
  zeroed_by_cascade: boolean;
  /** Сколько в итоге не объяснено показанными строками. Ноль — норма. */
  unexplained: number;
  tariff_lines: PayslipTariffLine[];
  deductions: PayslipDeduction[];
}

export interface PayslipOut {
  employee_id: string;
  employee_name: string;
  period_year: number;
  period_month: number;
  parts: PayslipPart[];
  debts: PayslipDebt[];
  advances: PayslipAdvance[];
  total_note?: string | null;
}

export function fetchPayslip(
  employeeId: string,
  params: { year: number; month: number },
): Promise<PayslipOut> {
  return request<PayslipOut>(
    `/api/v1/payslip/${encodeURIComponent(employeeId)}${queryString({ ...params })}`,
  );
}

/**
 * Расчётный лист файлом.
 *
 * Права на отчёты по компании тут не нужны: свой лист человек сохраняет и показывает сам, из
 * него он и понимает, почему из зарплаты ушло по 500 ₽.
 */
export function exportPayslip(
  employeeId: string,
  params: { year: number; month: number },
): Promise<FileResponse> {
  return requestFile(`/api/v1/payslip/${employeeId}/export${queryString(params)}`);
}

// ── Расчёт сотрудника: один человек за несколько месяцев и объектов ─────────────────────────
//
// Сводный документ поверх ведомостей (пакет Б4, 01.10.2026). Единицы денег он не заводит и ничего
// не пишет: деньги двигаются там же, где и раньше, — утверждением ведомости и отметкой выплаты в
// строке человека. Всё, что в нём написано, посчитал и сложил сервер; кабинет только показывает.
//
// Общей суммы по нескольким юрлицам здесь нет: `totals` — по строке на юрлицо.

export interface PayslipPeriodPerson {
  employee_id: string;
  name: string;
  status: string;
  status_title: string;
  /** Последний рабочий день в виде «2026-03-29». Пусто — человек не уволен. */
  fired_at?: string | null;
}

export interface PayslipPeriodRange {
  year_from: number;
  month_from: number;
  year_to: number;
  month_to: number;
  months: number;
  /** Границы выбрал сервер: в запросе их не было. */
  is_default: boolean;
}

export interface PayslipPeriodPayout {
  paid_at: string;
  amount: number;
}

/** Один месяц человека на одном объекте: колонки как в листе заказчика «Расчёт Шаврин». */
export interface PayslipPeriodBlock {
  payroll_id: string;
  payroll_employee_id: string;
  period_year: number;
  period_month: number;
  site_id?: string | null;
  /** Пусто у административной ведомости (аппарат управления). */
  site_name?: string | null;
  legal_entity_id: string;
  legal_entity_name: string;
  number?: string | null;
  status: string;
  /** Слово состояния приходит с сервера: копии справочника в кабинете не держим. */
  status_title: string;
  /** Утверждена и дальше: суммы пересчётом больше не меняются. */
  is_final: boolean;
  /** Пусто, когда часы не проставлены: это не «не выходил». Часы и норма — строкой. */
  hours?: string | null;
  norm_hours?: string | null;
  /** Сумма за период и тариф показаны, только когда тарифная строка одна. */
  period_amount?: number | null;
  rate?: number | null;
  accrued: number;
  bonus: number;
  accruals_total: number;
  advance: number;
  advance_carried?: number;
  official_total: number;
  /** Разбивки официальной части у ролей со скрытием в ответе НЕТ вовсе. */
  official_paid?: number;
  official_rest?: number;
  tickets: number;
  training: number;
  fine: number;
  uniform: number;
  other_deductions: number;
  deductions_total: number;
  total_payout: number;
  /** Плюс: вычеты не поместились в начисленное. Минус: удержано больше, чем показано строками. */
  adjustment: number;
  adjustment_note?: string | null;
  paid: number;
  /** Сколько ещё выплатить. Не отрицательно: переплата лежит отдельно. */
  remaining: number;
  overpaid: number;
  paid_note?: string | null;
  payouts: PayslipPeriodPayout[];
  calculated: boolean;
  not_counted?: string | null;
  zeroed_by_cascade: boolean;
  tariff_lines: PayslipTariffLine[];
  accrual_extras: PayslipDeduction[];
  deductions: PayslipDeduction[];
}

/**
 * Что делать с пробелом. `recalculate_payroll` не переход на экран, а действие (пересчитать
 * ведомость, чтобы внесённые часы встали в деньги): адреса у него нет, кабинет рисует кнопку.
 */
export type PayslipPeriodStepKind =
  'create_payroll' | 'open_timesheet' | 'add_person' | 'recalculate_payroll';

/** Куда идти, чтобы пробел закрыть. Адрес экрана собирает кабинет: сервер их не знает. */
export interface PayslipPeriodStep {
  kind: PayslipPeriodStepKind;
  label: string;
  site_id?: string | null;
  year: number;
  month: number;
  /** Ведомость, в которую добавляют человека либо которую пересчитывают. */
  payroll_id?: string | null;
}

export interface PayslipPeriodGap {
  /**
   * `hours_not_in_payroll`: часы человека в табеле уже есть (внесли итогом или отметили по
   * сменам), а в деньгах ведомости их ещё нет. Лечится пересчётом ведомости, а не табелем.
   */
  kind: 'no_payroll' | 'no_timesheet' | 'not_in_payroll' | 'hours_not_in_payroll';
  period_year: number;
  period_month: number;
  site_id?: string | null;
  site_name?: string | null;
  text: string;
  hint?: string | null;
  step: PayslipPeriodStep;
  /** Пусто — табеля за месяц не заводили вовсе. */
  timesheet_status?: string | null;
  /** Норма часов из назначения человека на этом объекте: подсказка, сколько вносить итогом. */
  norm_hours?: string | null;
  /**
   * Часы человека, уже записанные в табеле за этот месяц на этом объекте: «60.00». Пусто — часов
   * в табеле нет вовсе. «0.00» — внесённый ноль: «не выходил» и «не заполнено» разные факты.
   */
  timesheet_hours?: string | null;
  /** Часы отмечены по сменам: итогом за месяц их внести нельзя, они сложились бы вдвое. */
  timesheet_hours_by_shifts?: boolean;
  /**
   * Почему «Внести часы» недоступно, словами сервера: ведомость утверждена, несколько назначений,
   * часы уже по сменам. Пусто — можно (если есть право).
   */
  entry_blocked_reason?: string | null;
}

export interface PayslipPeriodAdvanceItem {
  claim_id: string;
  /** Календарная дата выдачи в виде «2026-03-07». */
  happened_on?: string | null;
  amount: number;
  title: string;
  /** Пусто, когда документ открыл сам человек. */
  issued_by_name?: string | null;
  legal_entity_id?: string | null;
  /** Выдан после последнего месяца документа: ни в одну его ведомость не попал. */
  after_period: boolean;
}

export interface PayslipPeriodAdvancesSummary {
  issued: number;
  counted: number;
  difference: number;
  note?: string | null;
}

export interface PayslipPeriodDebtLine {
  debt_id: string;
  debt_type: string;
  debt_type_title: string;
  happened_on?: string | null;
  amount: number;
  outstanding: number;
  status: string;
  /** Состояние словами («Активен», «Погашен»): приходит с сервера. */
  status_title: string;
  description?: string | null;
}

export interface PayslipPeriodUniformLine {
  debt_id: string;
  title: string;
  issued_on?: string | null;
  price: number;
  quantity: number;
  amount: number;
}

export interface PayslipPeriodFineLine {
  period_year: number;
  period_month: number;
  site_name?: string | null;
  amount: number;
  /** Основание видно только тем, кто разбирает расчёт. */
  basis?: string | null;
}

/** Итог по одному юрлицу: «Всего …» образца и «К выдаче за всё время». */
export interface PayslipPeriodTotals {
  legal_entity_id: string;
  legal_entity_name: string;
  blocks: number;
  hours?: string | null;
  accrued: number;
  bonus: number;
  accruals_total: number;
  advance: number;
  official_total: number;
  tickets: number;
  training: number;
  fine: number;
  uniform: number;
  other_deductions: number;
  deductions_total: number;
  adjustment: number;
  /** «К выдаче за всё время»: сумма итогов ведомостей. */
  to_issue: number;
  paid: number;
  /** Не отрицательно. */
  remaining: number;
  /** Из «осталось» — то, что лежит в ведомостях в работе: там сумма ещё может измениться. */
  remaining_unapproved: number;
  overpaid: number;
}

export interface PayslipPeriodWarning {
  code: string;
  text: string;
  period_year?: number | null;
  period_month?: number | null;
}

export interface PayslipPeriodOut {
  person: PayslipPeriodPerson;
  /** Пусто, когда у человека нет ни одной ведомости. */
  period?: PayslipPeriodRange | null;
  blocks: PayslipPeriodBlock[];
  gaps: PayslipPeriodGap[];
  advances: PayslipPeriodAdvanceItem[];
  advances_summary?: PayslipPeriodAdvancesSummary | null;
  uniform: PayslipPeriodUniformLine[];
  tickets: PayslipPeriodDebtLine[];
  training: PayslipPeriodDebtLine[];
  fines: PayslipPeriodFineLine[];
  debts: PayslipDebt[];
  totals: PayslipPeriodTotals[];
  warnings: PayslipPeriodWarning[];
  notes: string[];
  is_preliminary: boolean;
  preliminary_reasons: string[];
  empty_reason?: string | null;
}

/** Границы необязательны: не заданы — сервер берёт от первой ведомости до месяца увольнения. */
export interface PayslipPeriodParams {
  year_from?: number;
  month_from?: number;
  year_to?: number;
  month_to?: number;
}

export function fetchPayslipPeriod(
  employeeId: string,
  params: PayslipPeriodParams,
  signal?: AbortSignal,
): Promise<PayslipPeriodOut> {
  return request<PayslipPeriodOut>(
    `/api/v1/payslip/${encodeURIComponent(employeeId)}/period${queryString({ ...params })}`,
    { signal },
  );
}

/**
 * Расчёт сотрудника файлом: блоки по месяцам и объектам, сводка и таблицы в раскладке листа «Расчёт
 * Шаврин».
 *
 * Файл собирает сервер из ТОГО ЖЕ документа, что и экран, поэтому права и скрытые поля у него те
 * же, а отдельного права на выгрузку нет. Границы кабинет передаёт явно, те, что показал документ:
 * не заданные, они выбираются сервером заново, и между показом и выгрузкой могла появиться новая
 * ведомость, а файл разошёлся бы с экраном.
 */
export function exportPayslipPeriod(
  employeeId: string,
  params: PayslipPeriodParams,
): Promise<FileResponse> {
  return requestFile(
    `/api/v1/payslip/${encodeURIComponent(employeeId)}/period/export${queryString({ ...params })}`,
  );
}
